import React from 'react';
import { Wallet, Edit2, Globe } from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Select } from '../../components/Select';
import { CurrencySelect } from '../../components/CurrencySelect';
import { PAYMENT_APPS, getPaymentApp } from '../../services/paymentLinks';

interface SettingsDefaultsProps {
    paymentAppId: string;
    setPaymentAppId: (val: string) => void;
    paymentUsername: string;
    setPaymentUsername: (val: string) => void;
    isEditingPayment: boolean;
    setIsEditingPayment: (val: boolean) => void;
    onUpdatePayment: () => void;
    onCancelEditPayment: () => void;
    preferredCurrency: string;
    onCurrencyChange: (val: string) => void;
}

export const SettingsDefaults: React.FC<SettingsDefaultsProps> = ({
    paymentAppId,
    setPaymentAppId,
    paymentUsername,
    setPaymentUsername,
    isEditingPayment,
    setIsEditingPayment,
    onUpdatePayment,
    onCancelEditPayment,
    preferredCurrency,
    onCurrencyChange
}) => {
    return (
        <div className="p-4 bg-bg-card border-b border-border-default">
            <div className="flex items-start gap-3">
                <div className="rounded-full bg-green-500/15 p-2 text-green-600 dark:text-green-400">
                    <Wallet size={20} />
                </div>
                <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-bold text-text-heading">Default Payment Method</p>
                            <p className="text-xs text-text-muted">Auto-filled when you create tabs</p>
                        </div>
                        {!isEditingPayment && (
                            <button
                                onClick={() => { setIsEditingPayment(true); }}
                                className="text-text-faint hover:text-green-600 p-1"
                                aria-label="Edit Payment Method"
                            >
                                <Edit2 size={14} />
                            </button>
                        )}
                    </div>

                    {isEditingPayment ? (
                        <div className="space-y-3 pt-2">
                            <Select
                                label="App"
                                value={paymentAppId}
                                onChange={(e) => {
                                    setPaymentAppId(e.target.value);
                                    if (!e.target.value) setPaymentUsername('');
                                }}
                                options={[
                                    { value: '', label: 'None' },
                                    ...PAYMENT_APPS.map((app) => ({
                                        value: app.id,
                                        label: `${app.icon}  ${app.name}`,
                                    })),
                                ]}
                            />
                            {paymentAppId && (
                                <Input
                                    label={paymentAppId === 'custom' ? 'Payment URL' : 'Username / Handle'}
                                    value={paymentUsername}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPaymentUsername(e.target.value); }}
                                    placeholder={getPaymentApp(paymentAppId)?.placeholder ?? 'Enter username'}
                                />
                            )}
                            <div className="flex gap-2 justify-end">
                                <Button variant="ghost" size="sm" onClick={onCancelEditPayment}>Cancel</Button>
                                <Button size="sm" onClick={onUpdatePayment}>Save Settings</Button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 mt-1">
                            {paymentAppId ? (
                                <>
                                    <span className="text-sm">{getPaymentApp(paymentAppId)?.icon}</span>
                                    <p className="text-sm font-medium text-text-body">
                                        {getPaymentApp(paymentAppId)?.name}: <span className="text-text-muted">{paymentUsername}</span>
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-text-faint italic">No default payment method set</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border-default">
                <div className="rounded-full bg-blue-500/15 p-2 text-blue-600 dark:text-blue-400">
                    <Globe size={20} />
                </div>
                <div className="flex-1 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-bold text-text-heading">Preferred Currency</p>
                        <p className="text-xs text-text-muted">Default currency for new tabs</p>
                    </div>
                    <div className="w-24">
                        <CurrencySelect
                            value={preferredCurrency}
                            onChange={onCurrencyChange}
                            className="h-8 py-0 px-2 text-xs"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
